import { type GradeCategory, type Lesson } from "@core/grades/types";
import { trans, plainTrans } from "@mongez/localization";
import {
  BookOpenIcon,
  FilterIcon,
  SearchIcon,
  SparklesIcon,
} from "lucide-react";
import { type ChangeEvent } from "react";
import { SubjectLessonCard } from "./SubjectLessonCard";

type SubjectLessonsSectionProps = {
  activeCategoryId: number | null;
  categories: GradeCategory[];
  lessons: Lesson[];
  searchQuery: string;
  subjectName: string;
  totalLessons: number;
  onCategoryChange: (categoryId: number | null) => void;
  onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

export function SubjectLessonsSection({
  activeCategoryId,
  categories,
  lessons,
  searchQuery,
  subjectName,
  totalLessons,
  onCategoryChange,
  onSearchChange,
}: SubjectLessonsSectionProps) {
  return (
    <section
      id="subject-lessons"
      aria-labelledby="subject-lessons-title"
      className="scroll-mt-24 pt-10 sm:pt-14"
    >
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="mb-2 inline-flex items-center gap-1.5 text-xs font-extrabold text-primary-700 dark:text-primary-300">
            <SparklesIcon className="h-3.5 w-3.5" />
            {trans("grades.yourLearningPath")}
          </span>
          <h2
            id="subject-lessons-title"
            className="text-2xl font-black text-gray-950 dark:text-white sm:text-3xl"
          >
            {trans("grades.subjectCurriculum")}
          </h2>
          <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
            {trans("grades.lessonsInSubjectDescription", {
              count: totalLessons,
              subject: subjectName,
            })}
          </p>
        </div>
        <span className="dark:bg-primary-950/50 inline-flex w-fit items-center gap-2 rounded-2xl bg-primary-50 px-4 py-2 text-sm font-extrabold text-primary-700 dark:text-primary-300">
          <BookOpenIcon className="h-4 w-4" />
          {trans("grades.availableLessons", { count: lessons.length })}
        </span>
      </div>

      <div className="mb-7 rounded-[1.75rem] border border-gray-200/80 bg-white/90 p-4 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-gray-900/90 sm:p-5">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            type="search"
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={plainTrans("grades.searchSubjectLessons")}
            aria-label={plainTrans("grades.searchSubjectLessons")}
            className="dark:focus:ring-primary-950 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 pe-4 ps-11 text-sm font-medium text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-primary-400 focus:bg-white focus:ring-4 focus:ring-primary-100 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-primary-600 dark:focus:bg-gray-900"
          />
        </div>

        {categories.length > 0 ? (
          <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1">
            <FilterIcon className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />
            <button
              type="button"
              onClick={() => onCategoryChange(null)}
              className={
                activeCategoryId === null
                  ? "shrink-0 rounded-full bg-gray-950 px-4 py-2 text-xs font-bold text-white shadow-md dark:bg-white dark:text-gray-950"
                  : "shrink-0 rounded-full bg-gray-100 px-4 py-2 text-xs font-bold text-gray-600 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              }
            >
              {trans("grades.allSubjectLessons")}
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => onCategoryChange(category.id)}
                className={
                  activeCategoryId === category.id
                    ? "shrink-0 rounded-full bg-primary-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-primary-600/20"
                    : "shrink-0 rounded-full bg-gray-100 px-4 py-2 text-xs font-bold text-gray-600 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                }
              >
                {category.name}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {lessons.length === 0 ? (
        <div className="rounded-[1.75rem] border border-dashed border-gray-300 bg-white/80 px-5 py-14 text-center dark:border-gray-700 dark:bg-gray-900/80 sm:px-8">
          <span className="dark:bg-primary-950/50 mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-primary-50 text-primary-600 dark:text-primary-300">
            <BookOpenIcon className="h-8 w-8" />
          </span>
          <h3 className="mt-5 text-lg font-black text-gray-900 dark:text-white sm:text-xl">
            {trans("grades.noMatchingSubjectLessons")}
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-600 dark:text-gray-300">
            {trans("grades.noMatchingSubjectLessonsMessage")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          {lessons.map((lesson, index) => (
            <SubjectLessonCard key={lesson.id} index={index} lesson={lesson} />
          ))}
        </div>
      )}
    </section>
  );
}
